import React, { useEffect, useState } from "react";
import Shell from "@/components/Shell";
import api, { formatINR } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, Line, LineChart } from "recharts";

export default function Reports() {
  const [team, setTeam] = useState([]);
  const [trend, setTrend] = useState([]);
  const [period, setPeriod] = useState("month");

  useEffect(() => { api.get("/stats/team").then((r) => setTeam(r.data)); }, []);
  useEffect(() => { api.get(`/stats/trend?period=${period}`).then((r) => setTrend(r.data)); }, [period]);

  return (
    <Shell title="Reports" subtitle="Attributed performance (80% closure / 20% referral) and trends.">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <Card className="p-6 border-zinc-200 shadow-none rounded-md bg-white">
          <div className="mb-4">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Attribution</div>
            <h2 className="text-xl font-bold tracking-tight font-heading">Salesperson totals</h2>
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={team} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(v) => formatINR(v)} />
                <Bar dataKey="attributed_invoice_value" name="Invoice" fill="#1e3a8a" radius={[4, 4, 0, 0]} />
                <Bar dataKey="attributed_margin" name="Margin" fill="#1d4ed8" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-6 border-zinc-200 shadow-none rounded-md bg-white">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Trend</div>
              <h2 className="text-xl font-bold tracking-tight font-heading">{period === "month" ? "Monthly" : "Weekly"} trend</h2>
            </div>
            <div className="flex gap-1 text-xs bg-zinc-100 rounded-md p-1">
              <button onClick={() => setPeriod("month")} data-testid="period-month" className={`px-3 py-1 rounded ${period === "month" ? "bg-white shadow-sm font-semibold" : "text-zinc-600"}`}>Month</button>
              <button onClick={() => setPeriod("week")} data-testid="period-week" className={`px-3 py-1 rounded ${period === "week" ? "bg-white shadow-sm font-semibold" : "text-zinc-600"}`}>Week</button>
            </div>
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trend} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                <XAxis dataKey="period" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(v) => formatINR(v)} />
                <Line type="monotone" dataKey="invoice_value" stroke="#1e3a8a" strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="margin" stroke="#1d4ed8" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      <Card className="p-6 border-zinc-200 shadow-none rounded-md bg-white">
        <div className="mb-4">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Targets</div>
          <h2 className="text-xl font-bold tracking-tight font-heading">Target progress</h2>
        </div>
        <div className="space-y-5">
          {team.length === 0 && <div className="text-sm text-zinc-500">No salespeople.</div>}
          {team.map((sp) => {
            const val = sp.target_type === "turnover" ? sp.attributed_invoice_value : sp.attributed_margin;
            const pct = sp.target_amount > 0 ? Math.min(sp.progress_pct, 100) : 0;
            return (
              <div key={sp.id} data-testid={`report-row-${sp.id}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="text-sm font-semibold">{sp.name} <span className="text-xs text-zinc-500 ml-2 capitalize">({sp.target_type})</span></div>
                  <div className="text-sm tabular-nums">{formatINR(val)} / <span className="text-zinc-500">{formatINR(sp.target_amount)}</span> · {sp.progress_pct}%</div>
                </div>
                <Progress value={pct} className="h-2" />
              </div>
            );
          })}
        </div>
      </Card>
    </Shell>
  );
}
